import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { PublicComplaintSubmission, PublicComplaintSubmissionReceipt } from 'app/models/complaints/public-complaint-submission';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

type ComplaintApiPayload = {
  id: number;
  complaintDate: string;
  complaintProblem: string;
  complaintClosed: boolean;
  isAnonymous: boolean;
  complainteeFirstName: string;
  complainteeLastName: string;
  complainteePhone: string;
  complainteeEmail: string;
  complainteeAddress: string;
  complainteeCity: string;
  complainteeState: string;
  complainteeZip: string;
  complaintAssociationName: string;
  complainantAddress: string;
  complainantCity: string;
  complainantState: string;
  complainantCounty: string;
  complainantZip: string;
  status: string;
};

type ComplaintApiResponse = {
  id: number;
  complaintNumber: string;
  status: string;
  complaintDate?: string;
};

@Injectable({
  providedIn: 'root'
})
export class PublicComplaintService {
  private readonly api = environment.publicApi;

  constructor(private readonly httpClient: HttpClient) {}

  submitComplaint(submission: PublicComplaintSubmission): Observable<PublicComplaintSubmissionReceipt> {
    const payload = this.buildPayload(submission);

    return this.httpClient.post<ComplaintApiResponse>(this.api + 'Complaint/SubmitComplaint', payload).pipe(
      map((response) => ({
        id: response.id,
        complaintNumber: response.complaintNumber,
        status: response.status,
        complaintDate: response.complaintDate || payload.complaintDate,
      })),
    );
  }

  private buildPayload(submission: PublicComplaintSubmission): ComplaintApiPayload {
    const submitterFirstName = this.normalizeOptional(submission.submitterFirstName);
    const submitterLastName = this.normalizeOptional(submission.submitterLastName);
    const submitterPhone = this.normalizeOptional(submission.submitterPhone);
    const submitterEmail = this.normalizeOptional(submission.submitterEmail);
    const submitterAddressStreet = this.normalizeOptional(submission.submitterAddressStreet);
    const submitterAddressCity = this.normalizeOptional(submission.submitterAddressCity);
    const submitterAddressState = this.normalizeOptional(submission.submitterAddressState);
    const submitterAddressZip = this.normalizeOptional(submission.submitterAddressZip);
    const peopleInvolved = this.normalizeOptional(submission.peopleInvolved);
    const normalizedNarrative = this.normalizeOptional(submission.narrative);
    const normalizedSubmitterNames = this.normalizeSubmitterNames(submitterFirstName, submitterLastName);
    const complaintDate = new Date().toISOString();
    const hasSubmitterAddress = !!(submitterAddressStreet || submitterAddressCity || submitterAddressState || submitterAddressZip);

    const complaintProblemSections = [
      peopleInvolved ? 'People involved:\n' + peopleInvolved : '',
      normalizedNarrative,
    ].filter(Boolean);

    return {
      id: 0,
      complaintDate,
      complaintProblem: complaintProblemSections.join('\n\n'),
      complaintClosed: false,
      isAnonymous: !submitterFirstName && !submitterLastName && !submitterPhone && !submitterEmail && !hasSubmitterAddress,
      complainteeFirstName: normalizedSubmitterNames.firstName,
      complainteeLastName: normalizedSubmitterNames.lastName,
      complainteePhone: submitterPhone,
      complainteeEmail: submitterEmail,
      complainteeAddress: submitterAddressStreet,
      complainteeCity: submitterAddressCity,
      complainteeState: submitterAddressState,
      complainteeZip: submitterAddressZip,
      complaintAssociationName: peopleInvolved,
      complainantAddress: this.normalizeOptional(submission.locationStreet),
      complainantCity: this.normalizeOptional(submission.locationCity),
      complainantState: this.normalizeOptional(submission.locationState),
      complainantCounty: this.normalizeOptional(submission.locationCounty),
      complainantZip: this.normalizeOptional(submission.locationZip),
      status: 'Submitted',
    };
  }

  private normalizeOptional(value: string | null | undefined): string {
    return (value || '').trim();
  }

  private normalizeSubmitterNames(firstName: string, lastName: string): { firstName: string; lastName: string } {
    const hasFirstName = !!firstName;
    const hasLastName = !!lastName;

    if (!hasFirstName && !hasLastName) {
      return { firstName: 'Anonymous', lastName: 'Anonymous' };
    }

    return {
      firstName: hasFirstName ? firstName : 'Anonymous',
      lastName: hasLastName ? lastName : 'Anonymous',
    };
  }
}